import { useMemo } from "react";

const EMPTY_GROUPS = [];

export function buildQueueSliceIndexes(partitions) {
  const interactive = new Map();
  const batch = new Map();

  for (const partition of partitions ?? []) {
    for (const slice of partition.queue_slices ?? []) {
      const enrichedSlice = {
        ...slice,
        partition_name: partition.partition_name,
        partition_type: partition.partition_type,
        gpu_family: partition.gpu_family,
        gpu_memory_gb: partition.gpu_memory_gb,
      };

      if (slice.interactive_local_queue) {
        interactive.set(slice.interactive_local_queue, enrichedSlice);
      }
      if (slice.batch_local_queue) {
        batch.set(slice.batch_local_queue, enrichedSlice);
      }
    }
  }

  return { interactive, batch };
}

function resolveLiveSliceForAllocation(allocation, queueSlices) {
  if (!allocation) {
    return null;
  }

  return (
    queueSlices.interactive.get(allocation.interactive_local_queue) ??
    queueSlices.batch.get(allocation.batch_local_queue) ??
    null
  );
}

function buildResourceGroupRows(allGroups, userGroups, queueSlices) {
  const membershipById = new Map(
    (userGroups ?? []).map((group) => [group.id, group]),
  );

  return (allGroups ?? [])
    .map((group) => {
      const membership = membershipById.get(group.id) ?? null;
      const allocations = group.allocations ?? membership?.allocations ?? [];
      const resourceAllocations = allocations.length > 0 ? allocations : [null];
      const isAssigned = Boolean(membership);

      const resources = resourceAllocations.map((allocation, index) => {
        const liveSlice = resolveLiveSliceForAllocation(
          allocation,
          queueSlices,
        );

        return {
          id: `${group.id}-${index}`,
          allocation,
          liveSlice,
          partitionName:
            allocation?.partition_name ??
            liveSlice?.partition_name ??
            group.name,
          hasLiveData: Boolean(liveSlice),
          maxWalltimeInteractive: allocation?.max_walltime_interactive ?? null,
          maxWalltimeBatch: allocation?.max_walltime_batch ?? null,
        };
      });

      return {
        id: group.id,
        name: group.name,
        type: group.type,
        description: group.description,
        isAssigned,
        expiresAt: membership?.expires_at ?? null,
        hasLiveData: resources.some((resource) => resource.hasLiveData),
        resources,
      };
    })
    .sort((a, b) => {
      if (a.isAssigned !== b.isAssigned) {
        return a.isAssigned ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
}

export const useResourceAggregateByGroups = ({
  aggregate,
  allGroups = EMPTY_GROUPS,
  userGroups = EMPTY_GROUPS,
} = {}) => {
  const queueSlices = useMemo(
    () => buildQueueSliceIndexes(aggregate?.partitions),
    [aggregate],
  );

  const resourceGroups = useMemo(
    () => buildResourceGroupRows(allGroups, userGroups, queueSlices),
    [allGroups, userGroups, queueSlices],
  );

  return {
    queueSlices,
    resourceGroups,
  };
};
